import { useMemo, useState } from 'react';
import { useLocation } from "react-router-dom";

// Used by array.map, returns attribute objects with their respective options
const collectVariationOptions = (collection, options) => {
  for (const option of options) {
    const attributeId = option.attribute_definition_id;
    let attribute = collection.find(attribute => attribute.attribute_definition_id === attributeId);
    if (!attribute) {
      attribute = {
        name: option.name,
        attribute_definition_id: attributeId,
        options: [],
        index: collection.length,
      };
      collection.push(attribute);
    }

    // Don't add already existing option to attribute option list
    if (attribute.options.find(value => value.id === option.id)) continue;

    attribute.options.push(option);
  }

  return collection;
};

// Returns a value that is a function for array.find, finds attribute match among list of attributes
const findAttributeMatchAgainst = attribute => attributeMatch =>
  attributeMatch?.id === attribute?.id &&
  attributeMatch?.attribute_definition_id === attribute?.attribute_definition_id;

/**
 * Adds "isSelected" and "isSelectable" to each option by testing against currently selected attribute values
 *
 * @param variations All variation products
 * @param selectedVariationsMap Currently selected variation values
 * @returns {function(*, *, *): *} Returns a value that is a function for array.map
 */
const mapSelectStatesToVariations = (variations, selectedVariationsMap) => (attribute, som, collection) => {
  const attributesMatchAgainst = collection
    .filter(collectedAttribute => collectedAttribute.index < attribute.index)
    .map(collectedAttribute =>
      collectedAttribute.options.find(opt => selectedVariationsMap[opt.attribute_definition_id] === opt.id)
    );

  const foundMatches = variations.filter(productVariation => {
    const matchedAttributes = productVariation.attributes.filter(productAttribute => {
      return attributesMatchAgainst.find(findAttributeMatchAgainst(productAttribute));
    });
    return matchedAttributes.length === attribute.index;
  });

  for (const optionKey in attribute.options) {
    const option = attribute.options[optionKey];
    const attributeId = attribute.attribute_definition_id;

    // If option is in first attribute category then all options are selectable
    let isSelectable = option.attribute_definition_id === collection?.[0].attribute_definition_id;
    const isSelected = selectedVariationsMap[attributeId] === option.id;

    if (!isSelectable) {
      isSelectable = Boolean(foundMatches.find(match => match.attributes.find(findAttributeMatchAgainst(option))));
    }

    attribute.options[optionKey].isSelected = isSelected;
    attribute.options[optionKey].isSelectable = isSelectable;
  }

  return attribute;
};

// Returns initial state for currently selected variation by setting first variation attributes as selected
const createInitialVariationOptions = product => {
  const productVariation = product.product_variations?.[0];

  // We don't have any variations
  if (!productVariation) {
    console.warn('No product variations found!');
    return {};
  }

  const initialState = {};
  for (const option of productVariation.attributes) {
    initialState[option.attribute_definition_id] = option.id;
  }

  return initialState;
};

// Finds matching product by current values in newSelectedVariations and sets found product attributes as default
const setMissingVariationAttributes = (newSelectedVariations, product) => {
  const findMatchingAttributes = productAttribute => {
    return newSelectedVariations[productAttribute.attribute_definition_id] === productAttribute.id;
  };

  // Find first product that have matching attributes from newSelectedVariations
  const matchingProduct = product.product_variations.find(productVariation => {
    return (
      productVariation.attributes.filter(findMatchingAttributes).length === Object.keys(newSelectedVariations).length
    );
  });

  // Apply matching product variations selected as default
  if (matchingProduct) {
    for (const productAttribute of matchingProduct.attributes) {
      if (!newSelectedVariations[productAttribute.attribute_definition_id]) {
        newSelectedVariations[productAttribute.attribute_definition_id] = productAttribute.id;
      }
    }
  }

  return newSelectedVariations;
};

export function useProductVariations(product) {
  const { state } = useLocation();

  const [selectedVariationOptions, setSelectedVariations] = useState(createInitialVariationOptions(product));

  // Variation attribute map
  const variationOptions = useMemo(() => {
    if (!product?.product_variations?.length) return [];
    return product.product_variations
      .map(variation => variation.attributes)
      .reduce(collectVariationOptions, [])
      .map(mapSelectStatesToVariations(product.product_variations, selectedVariationOptions));
  }, [selectedVariationOptions, state]);

  const selectedVariation = useMemo(() => {
    if (!product?.product_variations?.length) return null;
    return product.product_variations.find(variation => {
      const filterSelected = attribute => selectedVariationOptions[attribute.attribute_definition_id] === attribute.id;
      // True if found variation product attribute's count equals attribute category count
      return variation.attributes.filter(filterSelected).length === Object.keys(selectedVariationOptions).length;
    });
  }, [selectedVariationOptions, state]);

  const onVariationSelect = (optionId, attributeId) => {
    const newSelectedVariationOptions = { ...selectedVariationOptions };

    // Remove all attributes that might be invalid
    let invalidate = false;
    for (const key in variationOptions) {
      const attribute = variationOptions[key];

      if (invalidate) delete newSelectedVariationOptions[attribute.attribute_definition_id];
      if (attribute.attribute_definition_id === attributeId) {
        invalidate = true;
        newSelectedVariationOptions[attributeId] = optionId;
      }
    }

    // Set missing attributes by matched product from existing values
    setMissingVariationAttributes(newSelectedVariationOptions, product);
    setSelectedVariations(newSelectedVariationOptions);
  };
  return {
    variationOptions,
    onVariationSelect,
    selectedVariation,
  };
}
